import { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { useChainId, useClient } from 'wagmi'
import { isEqual } from 'lodash'

import type {
  SportMarket,
  SportsAmmData,
  TicketMarket,
  TicketPosition,
} from '@/Overtime/types/markets'
import { StatusFilter } from '@/Overtime/enums/markets'
import useSportsMarketsV2Query from '@/Overtime/queries/useSportsMarketsV2Query'
import { isSameMarket, getTradeData } from '@/Overtime/utils/marketsV2'
import useSportsAmmDataQuery from '@/Overtime/queries/useSportsAmmDataQuery'
import useTicketLiquidityQuery from '@/Overtime/queries/useTicketLiquidityQuery'
import { SYSTEM_BET_MINIMUM_MARKETS } from '@/Overtime/constants/markets'
import { coinFormatter, coinParser } from 'thales-utils'
import { getContractInstance } from '@/Overtime/utils/contract'
import { ContractType } from '@/Overtime/enums/contract'
import { getSportsAMMV2QuoteMethod } from '@/Overtime/utils/sportsAmmV2'
import {
  getCollateral,
  getCollateralAddress,
} from '@/Overtime/utils/collaterals'
import { type SupportedNetwork } from '@/Overtime/types/network'
import useChainProvider from './useChainProvider'
import logger from '@/utils/logger'

const TicketErrorMessage = {
  RISK_PER_COMB: 'RiskPerComb exceeded',
  SAME_TEAM_IN_PARLAY: 'SameTeamOnParlay',
  PROOF_IS_NOT_VALID: 'Proof is not valid',
}

interface UseOvertimeBetProps {
  ticket: TicketPosition[]
  betAmount: string
  selectedCollateralIndex?: number
  systemBetDenominator?: number
}

interface UseOvertimeBetReturn {
  maxBet: number | undefined
  isMaxBetFetching: boolean
  ticketMarkets: TicketMarket[]
  unavailableMarkets: TicketPosition[]
  payout: number
  isFetching: boolean
  fetchTicketAmmQuote: (
    buyInAmountForQuote: number,
    fetchQuoteOnly: boolean
  ) => Promise<any>
}

export const useOvertimeBet = ({
  ticket,
  betAmount,
  selectedCollateralIndex = 0,
  systemBetDenominator = 2,
}: UseOvertimeBetProps): UseOvertimeBetReturn => {
  const client = useClient()
  const chainId = useChainId()
  const [ticketMarkets, setTicketMarkets] = useState<TicketMarket[]>([])
  const [unavailableMarkets, setUnavailableMarkets] = useState<
    TicketPosition[]
  >([])

  const { isOvertimeChain } = useChainProvider()

  const [payout, setPayout] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [isProofValid, setIsProofValid] = useState(true)

  const previousTicketOdds = useRef<
    { position: number; odd: number; gameId: string; proof: string[] }[]
  >([])

  const collateralAddress = useMemo(() => {
    if (!isOvertimeChain) {
      return undefined
    }
    return getCollateralAddress(
      chainId as SupportedNetwork,
      selectedCollateralIndex
    )
  }, [isOvertimeChain, chainId, selectedCollateralIndex])

  const noProofs = useMemo(
    () => ticketMarkets.every(market => !market.proof),
    [ticketMarkets]
  )

  const sportMarketsProofsQuery = useSportsMarketsV2Query(
    StatusFilter.OPEN_MARKETS,
    true,
    { networkId: chainId as SupportedNetwork },
    ticket,
    {
      enabled: !!ticket.length && !!isOvertimeChain,
    }
  )

  const openMarkets = sportMarketsProofsQuery.data?.[StatusFilter.OPEN_MARKETS]

  const sportMarketsQuery = useSportsMarketsV2Query(
    StatusFilter.OPEN_MARKETS,
    false,
    { networkId: chainId as SupportedNetwork },
    undefined,
    {
      enabled: !!openMarkets && !!isOvertimeChain,
    }
  )

  const sportMarkets = useMemo(() => {
    if (sportMarketsProofsQuery.isSuccess && sportMarketsProofsQuery.data) {
      return sportMarketsProofsQuery.data[StatusFilter.OPEN_MARKETS]
    }
    if (openMarkets) {
      return openMarkets
    }
    if (sportMarketsQuery.isSuccess && sportMarketsQuery.data) {
      return sportMarketsQuery.data[StatusFilter.OPEN_MARKETS]
    }
    return undefined
  }, [
    openMarkets,
    sportMarketsProofsQuery.data,
    sportMarketsProofsQuery.isSuccess,
    sportMarketsQuery.data,
    sportMarketsQuery.isSuccess,
  ])

  useEffect(() => {
    if (sportMarkets && Array.isArray(sportMarkets)) {
      const sportOpenMarkets = sportMarkets.reduce(
        (acc: SportMarket[], market: SportMarket) => {
          acc.push(market)
          market.childMarkets.forEach((childMarket: SportMarket) => {
            acc.push(childMarket)
          })
          return acc
        },
        []
      )

      const openTicketMarkets: TicketMarket[] = ticket
        .filter(ticketPosition =>
          sportOpenMarkets.some(
            (market: SportMarket) =>
              isSameMarket(market, ticketPosition) &&
              market.odds[ticketPosition.position] !== 0
          )
        )
        .map(ticketPosition => {
          const openMarket: SportMarket = sportOpenMarkets.filter(
            (market: SportMarket) => isSameMarket(market, ticketPosition)
          )[0]
          return {
            ...openMarket,
            position: ticketPosition.position,
            odd: openMarket.odds[ticketPosition.position],
          }
        })

      if (ticket.length > openTicketMarkets.length) {
        const notOpenedMarkets = ticket.filter(ticketPosition =>
          openTicketMarkets.every(
            (market: SportMarket) => !isSameMarket(market, ticketPosition)
          )
        )

        setUnavailableMarkets(notOpenedMarkets)
      } else {
        setUnavailableMarkets([])
      }

      const ticketOdds = openTicketMarkets.map(market => ({
        odd: market.odd,
        position: market.position,
        gameId: market.gameId,
        proof: market.proof,
      }))

      if (!isEqual(previousTicketOdds.current, ticketOdds)) {
        setTicketMarkets(openTicketMarkets)
        previousTicketOdds.current = ticketOdds
      }
    }
  }, [sportMarkets, ticket])

  const sportsAmmDataQuery = useSportsAmmDataQuery(
    { networkId: chainId as SupportedNetwork, client },
    { enabled: !!isOvertimeChain }
  )

  const sportsAmmData: SportsAmmData | undefined = useMemo(() => {
    if (sportsAmmDataQuery.isSuccess && sportsAmmDataQuery.data) {
      return sportsAmmDataQuery.data
    }
    if (sportsAmmDataQuery.isError) {
      logger.error({ sportsAmmDataQuery: sportsAmmDataQuery.error })
    }
    return undefined
  }, [sportsAmmDataQuery.isSuccess, sportsAmmDataQuery.data])

  const totalQuote = useMemo(() => {
    let quote = 0

    quote = ticketMarkets.reduce(
      (partialQuote, market) => partialQuote * market.odd,
      1
    )
    const maxSupportedOdds = sportsAmmData?.maxSupportedOdds || 1
    quote = quote && quote < maxSupportedOdds ? maxSupportedOdds : quote

    return quote
  }, [ticketMarkets, sportsAmmData?.maxSupportedOdds])

  const ticketLiquidityQuery = useTicketLiquidityQuery(
    ticketMarkets,
    false,
    2,
    false,
    totalQuote,
    { networkId: chainId as SupportedNetwork, client },
    { enabled: !noProofs }
  )

  const ticketLiquidity: number | undefined = useMemo(() => {
    if (
      ticketLiquidityQuery.isSuccess &&
      ticketLiquidityQuery.data !== undefined
    ) {
      let liquidity = ticketLiquidityQuery.data
      return liquidity
    }
    return undefined
  }, [
    ticketLiquidityQuery.isSuccess,
    ticketLiquidityQuery.data,
    ticketMarkets.length,
  ])

  const maxBet = useMemo(() => {
    if (ticketLiquidity) {
      return ticketLiquidity
    }
    return undefined
  }, [ticketLiquidity])

  const isMaxBetFetching = useMemo(() => {
    return (
      sportMarketsProofsQuery.isFetching ||
      sportMarketsQuery.isFetching ||
      sportsAmmDataQuery.isFetching ||
      ticketLiquidityQuery.isFetching
    )
  }, [
    sportMarketsProofsQuery.isFetching,
    sportMarketsQuery.isFetching,
    sportsAmmDataQuery.isFetching,
    ticketLiquidityQuery.isFetching,
  ])

  const fetchTicketAmmQuote = useCallback(
    async (buyInAmountForQuote: number, fetchQuoteOnly: boolean) => {
      const sportsAMMV2Contract = getContractInstance(
        ContractType.SPORTS_AMM_V2,
        {
          client,
          networkId: chainId as SupportedNetwork,
        }
      )

      if (sportsAMMV2Contract) {
        try {
          const tradeData = getTradeData(ticketMarkets)

          const [parlayAmmQuote] = await Promise.all([
            getSportsAMMV2QuoteMethod(
              collateralAddress,
              true,
              sportsAMMV2Contract,
              tradeData,
              coinParser(
                buyInAmountForQuote.toString(),
                chainId as SupportedNetwork,
                getCollateral(chainId as SupportedNetwork, 0)
              ),
              false,
              systemBetDenominator
            ),
          ])

          setIsProofValid(true)

          return {
            ...parlayAmmQuote,
            buyInAmountInDefaultCollateralNumber: coinFormatter(
              parlayAmmQuote.buyInAmountInDefaultCollateral,
              chainId as SupportedNetwork
            ),
          }
        } catch (e: any) {
          const errorMessage = e.error?.data?.message
          if (errorMessage) {
            if (errorMessage.includes(TicketErrorMessage.RISK_PER_COMB)) {
              return { error: TicketErrorMessage.RISK_PER_COMB }
            } else if (
              errorMessage.includes(TicketErrorMessage.SAME_TEAM_IN_PARLAY)
            ) {
              return { error: TicketErrorMessage.SAME_TEAM_IN_PARLAY }
            }
          } else if (
            e &&
            e.toString().includes(TicketErrorMessage.PROOF_IS_NOT_VALID)
          ) {
            setIsProofValid(false)
          }
          return { error: e }
        }
      }
    },
    [
      noProofs,
      betAmount,
      ticketMarkets,
      client,
      collateralAddress,
      systemBetDenominator,
    ]
  )

  // Add the useEffect for fetching ticket AMM quote
  useEffect(() => {
    let isSubscribed = true // Use for race condition

    const fetchData = async () => {
      setIsFetching(true)

      const minBuyInAmount = SYSTEM_BET_MINIMUM_MARKETS //TODO

      if (Number(betAmount) > 0 && minBuyInAmount) {
        const parlayAmmQuote = await fetchTicketAmmQuote(
          Number(betAmount),
          false
        )
        if (!parlayAmmQuote.error && isSubscribed) {
          const payout = coinFormatter(
            parlayAmmQuote[1],
            chainId as SupportedNetwork
          )
          setPayout(payout)
        }
      }
      if (isSubscribed) {
        setIsFetching(false)
      }
    }
    fetchData().catch(e => logger.error(e))

    return () => {
      isSubscribed = false
    }
  }, [fetchTicketAmmQuote, betAmount])

  return {
    maxBet,
    isMaxBetFetching,
    ticketMarkets,
    unavailableMarkets,
    payout,
    isFetching,
    fetchTicketAmmQuote,
  }
}
